import React from 'react'
import { toast, useAppTheme } from '@workwhile/ui'

interface CopyableCodeProps {
  /** The code value to display and copy to the clipboard. */
  value: string
  /** Optional override for the copy success toast message. */
  copyMessage?: string
  /** When true, the code block spans the full width of its container. */
  fullWidth?: boolean
}

export const CopyableCode = ({
  value,
  copyMessage = 'Copied to clipboard',
  fullWidth = false,
}: CopyableCodeProps) => {
  const { colors } = useAppTheme()

  const handleCopy = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault()
    e.stopPropagation()
    navigator.clipboard
      .writeText(value)
      .then(() => toast.success(copyMessage))
      .catch(() => toast.error('Failed to copy'))
  }

  return (
    <code
      role="button"
      tabIndex={0}
      title="Click to copy"
      onClick={handleCopy}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCopy(e)
        }
      }}
      style={{
        padding: '1px 6px',
        borderRadius: 4,
        backgroundColor: colors.neutrals[50],
        border: `1px solid ${colors.neutrals[100]}`,
        fontSize: '0.85em',
        fontWeight: 500,
        cursor: 'pointer',
        verticalAlign: 'baseline',
        wordBreak: 'break-all',
        whiteSpace: 'normal',
        ...(fullWidth
          ? {
              display: 'block',
              width: '100%',
              boxSizing: 'border-box',
            }
          : {}),
      }}
    >
      {value}
    </code>
  )
}
